import { DiscountCodeTypes } from "../../enum";
import { applyManualUnitPrice, calculateShippingPricing, clearHistoricalPromotionRoleForPricing, fromCents, isHistoricalPromotionProvenanceLine, isNoChargeReturnLine, } from "@washday/utilities";
import { calculateTotalTaxesIncluded } from "./calculateTotalTaxesIncluded";
import { calculateTotalTaxesOverPrice } from "./calculateTotalTaxesOverPrice";
import { applyDiscountToProducts, getCreditApplied, getProductLineTotals, normalizeTaxBreakdownToTotal, productsMeetMinimumSubtotal, } from "./helpers";
const getNormalizedId = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};
const getProductIdentity = (product) => {
    var _a;
    const directId = getNormalizedId((_a = product === null || product === void 0 ? void 0 : product._id) !== null && _a !== void 0 ? _a : product === null || product === void 0 ? void 0 : product.productId);
    if (directId) {
        return directId;
    }
    const storeProductId = product === null || product === void 0 ? void 0 : product.storeProductId;
    if (typeof storeProductId === "string") {
        return getNormalizedId(storeProductId);
    }
    return getNormalizedId(storeProductId === null || storeProductId === void 0 ? void 0 : storeProductId._id);
};
const getDiscountTargetIdentity = (target) => {
    var _a, _b;
    if (typeof target === "string") {
        return getNormalizedId(target);
    }
    return getNormalizedId((_b = (_a = target === null || target === void 0 ? void 0 : target._id) !== null && _a !== void 0 ? _a : target === null || target === void 0 ? void 0 : target.productId) !== null && _b !== void 0 ? _b : target === null || target === void 0 ? void 0 : target.storeProductId);
};
const hasMatchingDiscountTarget = (targets = [], product) => {
    const productId = getProductIdentity(product);
    if (!productId) {
        return false;
    }
    return (Array.isArray(targets) ? targets : []).some((target) => {
        return getDiscountTargetIdentity(target) === productId;
    });
};
export const calculateOrderTotal = (order, selectedCustomer, storeSettings, hasShippingCost, storeDiscounts = [], discountCodeObj, redeemPointsDiscount = 0 // 💡 NUEVO parámetro
) => {
    var _a, _b, _c, _d, _e, _f;
    try {
        order = Object.assign(Object.assign({}, order), { products: ((_a = order.products) !== null && _a !== void 0 ? _a : []).map((line) => applyManualUnitPrice(line, Boolean(order.express))) });
        const appliedOrderDiscounts = {};
        const returnPricing = {
            orderType: order.orderType,
            returnItemsChargeMode: order.returnItemsChargeMode,
        };
        const historicalPromotionProvenance = ((_b = order.buyAndGetProducts) !== null && _b !== void 0 ? _b : [])
            .filter(isHistoricalPromotionProvenanceLine)
            .map(clearHistoricalPromotionRoleForPricing);
        const pricingOrder = Object.assign(Object.assign({}, order), { products: [...((_c = order.products) !== null && _c !== void 0 ? _c : []), ...historicalPromotionProvenance], buyAndGetProducts: ((_d = order.buyAndGetProducts) !== null && _d !== void 0 ? _d : []).filter((line) => !isHistoricalPromotionProvenanceLine(line)) });
        const hasNoChargeReturnLine = [
            ...pricingOrder.products,
            ...pricingOrder.buyAndGetProducts,
        ].some((line) => isNoChargeReturnLine(returnPricing, line));
        const couponMissesMinimum = hasNoChargeReturnLine &&
            !productsMeetMinimumSubtotal(pricingOrder.products, Boolean(pricingOrder.express), Number((_e = discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.minimumOrderSubtotal) !== null && _e !== void 0 ? _e : 0), returnPricing);
        const effectiveDiscountCodeObj = couponMissesMinimum
            ? null
            : discountCodeObj;
        // For NUMBER coupons (fixed-amount) we must bake `discountAmount` per
        // line so the tax calculators can reduce the taxable base BEFORE
        // computing tax. `applyDiscountToProducts` handles both per-unit
        // (`applyOnceOnOrder=false`) and proportional allocation
        // (`applyOnceOnOrder=true`). For other coupon types, the tax calc derives
        // the discount via `discPercentageInteger` so we can skip the pre-pass.
        // Keep order.discountCode as the coupon-presence signal even when the
        // effective coupon object is null, so automatic discounts stay suppressed.
        let processedOrder = pricingOrder;
        if (effectiveDiscountCodeObj &&
            effectiveDiscountCodeObj.type === DiscountCodeTypes.NUMBER) {
            const { newOrderProds, buyAndGetProds } = applyDiscountToProducts(effectiveDiscountCodeObj, pricingOrder.products, Boolean(pricingOrder.express), storeDiscounts, selectedCustomer === null || selectedCustomer === void 0 ? void 0 : selectedCustomer.customer, returnPricing);
            processedOrder = Object.assign(Object.assign({}, pricingOrder), { products: newOrderProds, buyAndGetProducts: buyAndGetProds && buyAndGetProds.length > 0
                    ? buyAndGetProds
                    : pricingOrder.buyAndGetProducts });
        }
        const productTableCalculator = (storeSettings === null || storeSettings === void 0 ? void 0 : storeSettings.taxesType) === 'over_price'
            ? calculateTotalTaxesOverPrice
            : calculateTotalTaxesIncluded;
        const productTableImports = productTableCalculator(processedOrder, selectedCustomer, storeSettings, storeDiscounts, appliedOrderDiscounts, effectiveDiscountCodeObj);
        // === PRODUCT LINE TOTALS ===
        const { totalQuantity, totalImportWithDiscount, totalImportWithoutDiscount, totalImporTaxes, totalDiscountAmount, totalSubtotalAmount, taxBreakdown, totalNetAfterDiscount, totalGrossBeforeDiscount, } = getProductLineTotals(productTableImports);
        // NOTE: the old post-tax "discountCodeAmount" subtraction was removed.
        // NUMBER coupons (both `applyOnceOnOrder=true` and `=false`) are now
        // applied pre-tax via per-line `discountAmount` baked above, so
        // `totalImportWithDiscount` already reflects the coupon AND its tax
        // reduction effect. `totalDiscountAmount` from the line totals is the
        // nominal coupon value (user-facing "Descuento").
        // === SHIPPING COST ===
        const shippingPricing = calculateShippingPricing({
            store: storeSettings,
            hasShippingCost,
            isFreeDelivery: (effectiveDiscountCodeObj === null || effectiveDiscountCodeObj === void 0 ? void 0 : effectiveDiscountCodeObj.type) === DiscountCodeTypes.FREE_DELIVERY,
        });
        const shippingCost = fromCents(shippingPricing.shippingServiceTotal);
        const orderTaxesTotal = +(totalImporTaxes + fromCents(shippingPricing.shippingTaxesTotal)).toFixed(2);
        const orderTaxBreakdown = normalizeTaxBreakdownToTotal([
            ...taxBreakdown,
            ...shippingPricing.taxBreakdown.map((item) => (Object.assign(Object.assign({}, item), { amount: fromCents(item.amount) }))),
        ], orderTaxesTotal);
        // === TOTAL ANTES DE CRÉDITOS/PUNTOS ===
        const orderTotalWithOutCredit = +(totalImportWithDiscount +
            shippingCost).toFixed(2);
        // === APLICAR CRÉDITO Y PUNTOS REDIMIDOS ===
        const creditApplied = getCreditApplied(selectedCustomer, orderTotalWithOutCredit);
        const requestedRedeemPointsDiscount = Number(redeemPointsDiscount);
        const redeemPointsDiscountApplied = hasNoChargeReturnLine
            ? +Math.min(Number.isFinite(requestedRedeemPointsDiscount)
                ? Math.max(0, requestedRedeemPointsDiscount)
                : 0, Math.max(0, orderTotalWithOutCredit - creditApplied)).toFixed(2)
            : redeemPointsDiscount;
        const orderTotal = +(orderTotalWithOutCredit -
            creditApplied -
            redeemPointsDiscountApplied).toFixed(2);
        // === PRICING BREAKDOWN ===
        // For included: totalImportWithoutDiscount=gross-before-discount, totalSubtotalAmount=net-before-discount
        // For over_price: totalImportWithoutDiscount=net-before-discount, totalSubtotalAmount=net-after-discount
        const taxMode = (storeSettings === null || storeSettings === void 0 ? void 0 : storeSettings.taxesType) === 'over_price' ? 'over_price' : 'included';
        const subtotalBeforeDiscountNet = taxMode === 'included' ? totalSubtotalAmount : totalImportWithoutDiscount;
        const subtotalAfterDiscountNet = taxMode === 'included' ? totalNetAfterDiscount : totalSubtotalAmount;
        const subtotalBeforeDiscountGross = taxMode === 'included' ? totalImportWithoutDiscount : totalGrossBeforeDiscount;
        const subtotalAfterDiscountGross = totalImportWithDiscount;
        const pricingBreakdown = {
            subtotalBeforeDiscountNet,
            subtotalAfterDiscountNet,
            subtotalBeforeDiscountGross,
            subtotalAfterDiscountGross,
            discountTotalNet: +(subtotalBeforeDiscountNet - subtotalAfterDiscountNet).toFixed(2),
            discountTotalGross: +(subtotalBeforeDiscountGross - subtotalAfterDiscountGross).toFixed(2),
            taxesTotal: orderTaxesTotal,
            shippingServiceTotal: shippingCost,
            creditApplied,
            redeemPointsApplied: redeemPointsDiscountApplied,
            totalBeforePaymentsGross: orderTotalWithOutCredit,
            total: orderTotal,
        };
        // For included: non-NUMBER lineDiscountAmount is net (discountAmountPerUnit*qty), so
        // totalDiscountAmount is mixed-semantic. Use discountTotalGross (gross-before minus gross-after)
        // which is unambiguously user-facing in both modes.
        const displayDiscount = taxMode === 'included' ? pricingBreakdown.discountTotalGross : pricingBreakdown.discountTotalNet;
        const displayTotals = {
            subtotal: totalImportWithoutDiscount, // display-safe: gross (included) or net (over_price) — both equal the price-tag amount
            discount: displayDiscount,
            taxesTotal: orderTaxesTotal,
            taxesLabel: taxMode === 'included' ? 'taxesIncluded' : 'taxesTotal',
            shippingServiceTotal: shippingCost,
            creditApplied,
            redeemPointsApplied: redeemPointsDiscountApplied,
            total: orderTotal,
        };
        const lineDisplayTotals = productTableImports.map((line) => {
            var _a, _b;
            return ({
                lineId: String((_b = (_a = line.product.lineId) !== null && _a !== void 0 ? _a : line.product.orderProductLineID) !== null && _b !== void 0 ? _b : ""),
                subtotal: +Number(line.productLineImportTotal).toFixed(2),
                discount: +(taxMode === "included"
                    ? line.productLineImportTotal - line.productLineTotalWithDiscount
                    : line.productLineImportTotal - line.productLineSubtotal).toFixed(2),
                taxesTotal: line.productLineTaxesTotal,
                total: line.productLineTotalWithDiscount,
            });
        });
        // === RETURN FINAL OBJECT ===
        return Object.assign(Object.assign({}, order), { totalQuantity, customerDiscount: processedOrder.discountCode ? 0 : (_f = selectedCustomer === null || selectedCustomer === void 0 ? void 0 : selectedCustomer.customer) === null || _f === void 0 ? void 0 : _f.discount, productTotal: totalImportWithDiscount, taxesTotal: orderTaxesTotal, taxBreakdown: orderTaxBreakdown, total: orderTotal, creditApplied, redeemPointsApplied: redeemPointsDiscountApplied, productTotalWithoutDiscount: totalImportWithoutDiscount, totalDiscountAmount: totalDiscountAmount, shippingServiceTotal: shippingCost, appliedOrderDiscounts, subtotal: totalSubtotalAmount, pricingBreakdown,
            displayTotals,
            lineDisplayTotals });
    }
    catch (error) {
        throw error;
    }
};
